/**
 * Attendance List — 서명자: 1) 화면 Chairman, 2) Chairman과 중복 제거한 주주 전원.
 * Consent 등 — Shareholder(Member) + Director 합집합, CONTACT_CD / 이름 교차 중복 제거.
 */
import {
  dedupePresetSigners,
  normalizeContactRegNoForDedup,
  normalizeSignerNameForDedup,
} from '../../../presetSignerDedup';
import type { PresetDocCustSigner } from '../../../presetDocCustSave';
import type { AgmEntityPersonRow, AgmUfsFormModel } from './useFormPayload';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** CONTACT_REG_NO 보조 비교용 — 하이픈·공백 제거, 대문자 */
export { normalizeContactRegNoForDedup };

/** 중복 제거 키 — CONTACT_CD 우선 (디렉터·주주 동일인) */
export function dedupKeyForAgmSigner(row: AgmEntityPersonRow): string {
  const cd = trim(row.contactCd);
  if (cd) return `cd:${cd.toUpperCase()}`;
  const reg = normalizeContactRegNoForDedup(row.contactRegNo || '');
  if (reg) return `reg:${reg}`;
  return `name:${normalizeSignerNameForDedup(row.name)}`;
}

export function mapOfficerFromApi(
  raw: Record<string, unknown>,
  role: 'director' | 'shareholder',
): AgmEntityPersonRow {
  const name = trim(
    raw?.name
    || raw?.contactName
    || `${raw?.firstName || ''} ${raw?.surname || raw?.lastName || ''}`.trim(),
  );
  return {
    name,
    contactRegNo: trim(raw?.contactRegNo || raw?.CONTACT_REG_NO || raw?.contactIdUen || raw?.CONTACT_ID_UEN),
    contactCd: trim(raw?.contactCd || raw?.CONTACT_CD),
    email: trim(raw?.contactEmail || raw?.email),
    role,
  };
}

function mergeAgmSignerRow(prev: AgmEntityPersonRow, row: AgmEntityPersonRow): void {
  if (row.role === 'director') prev.role = 'director';
  if (!trim(prev.email) && trim(row.email)) prev.email = trim(row.email);
  if (!trim(prev.contactRegNo) && trim(row.contactRegNo)) {
    prev.contactRegNo = trim(row.contactRegNo);
  }
  if (!trim(prev.contactCd) && trim(row.contactCd)) prev.contactCd = trim(row.contactCd);
}

function findRowIndexByPerson(rows: AgmEntityPersonRow[], row: AgmEntityPersonRow): number {
  const normName = normalizeSignerNameForDedup(row.name);
  const byName = rows.findIndex((r) => normalizeSignerNameForDedup(r.name) === normName);
  if (byName >= 0) return byName;
  const key = dedupKeyForAgmSigner(row);
  return rows.findIndex((r) => dedupKeyForAgmSigner(r) === key);
}

function appendRowIfNew(rows: AgmEntityPersonRow[], row: AgmEntityPersonRow): void {
  const name = trim(row.name);
  if (!name) return;
  const idx = findRowIndexByPerson(rows, { ...row, name });
  if (idx >= 0) {
    mergeAgmSignerRow(rows[idx], { ...row, name });
    return;
  }
  rows.push({ ...row, name });
}

function findEntityMemberMatch(m: AgmUfsFormModel, memberName: string): AgmEntityPersonRow | undefined {
  const norm = normalizeSignerNameForDedup(memberName);
  return (m.entityMembers || []).find((em) => normalizeSignerNameForDedup(em.name) === norm);
}

function hasPersonWithName(rows: AgmEntityPersonRow[], name: string): boolean {
  const norm = normalizeSignerNameForDedup(name);
  return rows.some((r) => normalizeSignerNameForDedup(r.name) === norm);
}

/** sole member — entityMembers·memberName 중복 append 방지, contactCd 보존 */
function appendSoleMemberNameIfMissing(rows: AgmEntityPersonRow[], m: AgmUfsFormModel): void {
  if (m.memberType !== 'sole_corporate' && m.memberType !== 'sole_individual') return;
  const memberName = trim(m.memberName);
  if (!memberName || hasPersonWithName(rows, memberName)) return;
  const match = findEntityMemberMatch(m, memberName);
  appendRowIfNew(rows, {
    name: memberName,
    contactCd: match?.contactCd,
    contactRegNo: match?.contactRegNo,
    email: match?.email,
    role: 'shareholder',
  });
}

/**
 * Member → Director 순 수집 후 CONTACT_CD / 이름 교차 1인 1회.
 * 동일 CONTACT_CD 이면 director role 우선(이메일 등 빈 필드 보완).
 */
export function dedupeAgmAttendanceSigners(
  rows: AgmEntityPersonRow[],
  options?: { chairmanName?: string },
): PresetDocCustSigner[] {
  return dedupePresetSigners(rows, {
    chairmanName: options?.chairmanName,
    defaultRole: 'shareholder',
  });
}

/** @deprecated CONTACT_CD 기준 dedupeAgmAttendanceSigners 사용 */
export const dedupeAgmSignersByContactRegNo = dedupeAgmAttendanceSigners;

/** Attendance List 서명 대상: entity 스냅샷(주주+이사) + 필요 시 폼 보조 */
export function collectAgmAttendanceSignerRows(m: AgmUfsFormModel): AgmEntityPersonRow[] {
  const rows: AgmEntityPersonRow[] = [];

  if (m.memberType === 'multiple') {
    for (const mem of m.members || []) {
      appendRowIfNew(rows, {
        name: trim(mem.name),
        contactCd: trim(mem.contactCd),
        role: 'shareholder',
      });
    }
    for (const dir of m.entityDirectors || []) {
      appendRowIfNew(rows, { ...dir, role: 'director' });
    }
    return rows;
  }

  for (const mem of m.entityMembers || []) {
    appendRowIfNew(rows, { ...mem, role: 'shareholder' });
  }
  for (const dir of m.entityDirectors || []) {
    appendRowIfNew(rows, { ...dir, role: 'director' });
  }

  const hasEntitySnapshot =
    (m.entityMembers?.length || 0) > 0 || (m.entityDirectors?.length || 0) > 0;

  if (hasEntitySnapshot) {
    appendSoleMemberNameIfMissing(rows, m);
    return rows;
  }

  if (m.memberType === 'sole_corporate' || m.memberType === 'sole_individual') {
    appendSoleMemberNameIfMissing(rows, m);
  } else {
    for (const mem of m.members || []) {
      appendRowIfNew(rows, {
        name: trim(mem.name),
        contactCd: trim(mem.contactCd),
        role: 'shareholder',
      });
    }
  }

  return rows;
}

/**
 * Consent to Short Notice — memberType 별 서명 대상
 *  · multiple: 주주 전원
 *  · sole_corporate: Corporate Representative (member_representative_name)
 *  · sole_individual: 단독 주주 (member_name)
 */
export function collectAgmConsentSignerRows(m: AgmUfsFormModel): AgmEntityPersonRow[] {
  const rows: AgmEntityPersonRow[] = [];

  if (m.memberType === 'sole_corporate') {
    const repName = trim(m.memberRepresentativeName);
    if (!repName) return rows;
    const corpMatch = findEntityMemberMatch(m, trim(m.memberName));
    appendRowIfNew(rows, {
      name: repName,
      contactCd: corpMatch?.contactCd,
      contactRegNo: corpMatch?.contactRegNo,
      email: corpMatch?.email,
      role: 'shareholder',
    });
    return rows;
  }

  if (m.memberType === 'sole_individual') {
    const memberName = trim(m.memberName);
    if (!memberName) return rows;
    const match = findEntityMemberMatch(m, memberName);
    appendRowIfNew(rows, {
      name: memberName,
      contactCd: match?.contactCd,
      contactRegNo: match?.contactRegNo,
      email: match?.email,
      role: 'shareholder',
    });
    return rows;
  }

  for (const mem of m.entityMembers || []) {
    appendRowIfNew(rows, { ...mem, role: 'shareholder' });
  }
  if ((m.entityMembers?.length || 0) > 0) {
    return rows;
  }
  for (const mem of m.members || []) {
    appendRowIfNew(rows, {
      name: trim(mem.name),
      contactCd: trim(mem.contactCd),
      role: 'shareholder',
    });
  }
  return rows;
}

/** Attendance 등 — 주주 전원 (Consent 제외·sole 시 member_name 보조) */
export function collectAgmShareholderSignerRows(m: AgmUfsFormModel): AgmEntityPersonRow[] {
  const rows: AgmEntityPersonRow[] = [];

  for (const mem of m.entityMembers || []) {
    appendRowIfNew(rows, { ...mem, role: 'shareholder' });
  }

  if ((m.entityMembers?.length || 0) > 0) {
    appendSoleMemberNameIfMissing(rows, m);
    return rows;
  }

  if (m.memberType === 'sole_corporate' || m.memberType === 'sole_individual') {
    appendSoleMemberNameIfMissing(rows, m);
  } else {
    for (const mem of m.members || []) {
      appendRowIfNew(rows, {
        name: trim(mem.name),
        contactCd: trim(mem.contactCd),
        role: 'shareholder',
      });
    }
  }

  return rows;
}

function buildSignerJsonFromRows(
  rows: AgmEntityPersonRow[],
  options?: { chairmanName?: string; forceShareholderRole?: boolean },
): { signers: string; Signers: string } | null {
  let list = dedupeAgmAttendanceSigners(rows, { chairmanName: options?.chairmanName });
  if (options?.forceShareholderRole) {
    list = list.map((s) => ({ ...s, role: 'shareholder' as const }));
  }
  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

function buildSingleSignerJson(
  nameRaw: unknown,
  role: string,
  options?: { chairmanName?: string },
): { signers: string; Signers: string } | null {
  const name = trim(nameRaw);
  if (!name) return null;
  const list = dedupePresetSigners([{ name, email: '', role }], options);
  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

/** Chairman 과 동일인 여부 (CONTACT_CD / REG / 이름) */
function isSameAgmPerson(a: AgmEntityPersonRow, b: AgmEntityPersonRow): boolean {
  if (dedupKeyForAgmSigner(a) === dedupKeyForAgmSigner(b)) return true;
  const na = normalizeSignerNameForDedup(trim(a.name));
  const nb = normalizeSignerNameForDedup(trim(b.name));
  return na.length > 0 && na === nb;
}

/** 화면 chairman_name — 엔티티·폼에서 contactCd 등 보조 매칭 */
function resolveChairmanReferenceRow(m: AgmUfsFormModel, chairmanName: string): AgmEntityPersonRow {
  const norm = normalizeSignerNameForDedup(chairmanName);
  for (const dir of m.entityDirectors || []) {
    if (normalizeSignerNameForDedup(trim(dir.name)) === norm) return { ...dir };
  }
  for (const mem of m.entityMembers || []) {
    if (normalizeSignerNameForDedup(trim(mem.name)) === norm) {
      return { ...mem, role: 'shareholder' };
    }
  }
  if (m.memberType === 'multiple') {
    for (const mem of m.members || []) {
      if (normalizeSignerNameForDedup(trim(mem.name)) === norm) {
        return {
          name: trim(mem.name),
          contactCd: trim(mem.contactCd),
          role: 'shareholder',
        };
      }
    }
  }
  if (m.memberType === 'sole_corporate' || m.memberType === 'sole_individual') {
    const memberName = trim(m.memberName);
    if (memberName && normalizeSignerNameForDedup(memberName) === norm) {
      const match = findEntityMemberMatch(m, memberName);
      return {
        name: memberName,
        contactCd: match?.contactCd,
        contactRegNo: match?.contactRegNo,
        email: match?.email,
        role: 'shareholder',
      };
    }
  }
  return { name: chairmanName };
}

function withoutChairmanDuplicates(
  rows: AgmEntityPersonRow[],
  chairmanRef: AgmEntityPersonRow,
): AgmEntityPersonRow[] {
  return rows.filter((r) => !isSameAgmPerson(r, chairmanRef));
}

/** Attendance List — Chairman 1번째 + (Chairman 제외) 주주 전원 signers/Signers JSON */
export function buildAgmUfsAttendanceSignerVariables(
  m: AgmUfsFormModel,
): { signers: string; Signers: string } | null {
  const chairmanName = trim(m.chairmanName);
  if (!chairmanName) return null;

  const chairmanRef = resolveChairmanReferenceRow(m, chairmanName);
  const shareholderRows = withoutChairmanDuplicates(
    collectAgmShareholderSignerRows(m),
    chairmanRef,
  );

  const shareholders = dedupePresetSigners(
    shareholderRows.map((r) => ({
      name: trim(r.name),
      email: trim(r.email),
      role: 'shareholder',
      contactCd: trim(r.contactCd),
      contactRegNo: trim(r.contactRegNo),
    })),
    { defaultRole: 'shareholder' },
  );

  const list: PresetDocCustSigner[] = [
    {
      name: chairmanName,
      email: trim(chairmanRef.email),
      role: 'chairman',
    },
    ...shareholders,
  ];

  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

/** Minutes of AGM (Multiple) — chairman_name 1명 */
export function buildAgmUfsMinutesMultipleChairmanSignerVariables(
  m: AgmUfsFormModel,
): { signers: string; Signers: string } | null {
  const chairmanName = trim(m.chairmanName);
  return buildSingleSignerJson(chairmanName, 'chairman', { chairmanName });
}

/** Minutes of AGM (Sole Individual) — sole member(주주) 1명 */
export function buildAgmUfsMinutesSoleIndividualSignerVariables(
  m: AgmUfsFormModel,
): { signers: string; Signers: string } | null {
  return buildSingleSignerJson(trim(m.memberName), 'shareholder');
}

/** Minutes of AGM (Sole Corporate) — corporate member 대표(주주) 1명 */
export function buildAgmUfsMinutesSoleCorporateSignerVariables(
  m: AgmUfsFormModel,
): { signers: string; Signers: string } | null {
  const name = trim(m.memberRepresentativeName) || trim(m.memberName);
  return buildSingleSignerJson(name, 'shareholder');
}

/** Consent to Short Notice.docx — memberType 별 서명자 signers/Signers JSON */
export function buildAgmUfsConsentSignerVariables(
  m: AgmUfsFormModel,
): { signers: string; Signers: string } | null {
  return buildSignerJsonFromRows(collectAgmConsentSignerRows(m), {
    chairmanName: trim(m.chairmanName),
    forceShareholderRole: true,
  });
}
